import { useEffect, useMemo, useRef, useState } from 'react'
import useSWR from 'swr'
import { useToast } from '../contexts/ToastContext'
import { useDebounce } from '../hooks'
import {
  fetchPromotion,
  fetchPromotionOffers,
  fetchConflicts,
  resolveCoupon,
  createPromotion,
  updatePromotion,
  discountedPrice,
  formatMoney,
  type Coupon,
  type CleengOffer,
  type OfferConflict,
} from '../services/promotionsApi'
import { Badge, Button, Drawer } from '../components'
import formStyles from '../styles/forms.module.scss'
import styles from './PromotionForm.module.scss'

interface PromotionFormProps {
  isOpen: boolean
  /** Promotion id to edit, or null to create a new one. */
  promotionId: string | null
  onClose: () => void
  onSaved: () => void
}

interface FormState {
  couponCode: string
  startsAt: string // datetime-local value
  endsAt: string
  enabled: boolean
}

const EMPTY_FORM: FormState = { couponCode: '', startsAt: '', endsAt: '', enabled: false }

// ISO <-> <input type="datetime-local"> (which is local, no timezone suffix).
const isoToLocalInput = (iso: string | null): string => {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

const localInputToIso = (local: string): string | null =>
  local ? new Date(local).toISOString() : null

// Cleeng's live status is "activated" (not "active"); accept both, matching the
// Go and Python clients.
const ACTIVE_COUPON_STATUSES = new Set(['activated', 'active'])

const couponStatusVariant = (status: string) =>
  ACTIVE_COUPON_STATUSES.has(status) ? 'success' : status === 'ended' ? 'neutral' : 'warning'

// Discount fraction → whole-or-two-decimal percentage (0.2999 → 29.99).
const pct = (fraction: number): number => Number((fraction * 100).toFixed(2))

export default function PromotionForm({ isOpen, promotionId, onClose, onSaved }: PromotionFormProps) {
  const { toast } = useToast()
  const isEdit = promotionId !== null

  const { data: existing } = useSWR(
    isEdit ? ['promotion', promotionId] : null,
    () => fetchPromotion(promotionId!),
    { revalidateOnFocus: false }
  )

  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [coupon, setCoupon] = useState<Coupon | null>(null)
  const [couponLoading, setCouponLoading] = useState(false)
  const [couponError, setCouponError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  // The offers this coupon covers, from the backend (the single eligibility
  // evaluator). The promotion applies to all of them automatically; the user does
  // not pick. This also drives the paywall preview and the enable guard.
  const { data: eligibleData } = useSWR(
    isOpen && coupon ? ['promotion-eligible-offers', coupon.code] : null,
    () => fetchPromotionOffers(coupon!.code),
    { revalidateOnFocus: false }
  )
  const eligibleOffers: CleengOffer[] = useMemo(() => eligibleData?.offers ?? [], [eligibleData])
  const noOffers = eligibleData !== undefined && eligibleOffers.length === 0

  // Which promotion actually wins each offer during this promotion's window.
  // Fold this (possibly unsaved) coupon + window into the conflict check,
  // excluding this promotion's own stored row when editing. Debounced so typing
  // in the date fields doesn't fire a request per keystroke.
  const debouncedStarts = useDebounce(form.startsAt, 400)
  const debouncedEnds = useDebounce(form.endsAt, 400)
  const { data: conflictsData } = useSWR(
    isOpen && coupon
      ? ['promotion-conflicts', coupon.code, debouncedStarts, debouncedEnds, promotionId ?? '']
      : null,
    () =>
      fetchConflicts({
        couponCode: coupon!.code,
        startsAt: localInputToIso(debouncedStarts),
        endsAt: localInputToIso(debouncedEnds),
        excludeId: promotionId ?? undefined,
      }),
    { revalidateOnFocus: false }
  )
  const conflictByOffer = useMemo(() => {
    const map = new Map<string, OfferConflict>()
    for (const c of conflictsData?.conflicts ?? []) map.set(c.offer_code, c)
    return map
  }, [conflictsData])

  const contestedCount = eligibleOffers.filter((o) => conflictByOffer.has(o.id)).length
  const overriddenCount = eligibleOffers.filter((o) => {
    const cand = conflictByOffer.get(o.id)?.promotions.find((p) => p.is_candidate)
    return cand ? !cand.wins : false
  }).length

  const loadCoupon = async (code: string) => {
    const trimmed = code.trim()
    if (!trimmed) return
    setCouponLoading(true)
    setCouponError(null)
    try {
      setCoupon(await resolveCoupon(trimmed))
    } catch (err) {
      setCoupon(null)
      setCouponError(err instanceof Error ? err.message : 'Failed to resolve coupon')
    } finally {
      setCouponLoading(false)
    }
  }

  // Seed the form once per opened promotion (mirrors FeedDetail's seed ref).
  const seededId = useRef<string | null | undefined>(undefined)
  useEffect(() => {
    if (!isOpen) {
      seededId.current = undefined
      return
    }
    if (!isEdit && seededId.current !== '') {
      seededId.current = ''
      setForm(EMPTY_FORM)
      setCoupon(null)
      setCouponError(null)
      return
    }
    if (isEdit && existing && seededId.current !== existing.tc_promotion_id) {
      seededId.current = existing.tc_promotion_id
      setForm({
        couponCode: existing.coupon_code,
        startsAt: isoToLocalInput(existing.starts_at),
        endsAt: isoToLocalInput(existing.ends_at),
        enabled: existing.enabled,
      })
      // Auto-resolve the saved coupon so the summary and preview populate.
      void loadCoupon(existing.coupon_code)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isEdit, existing])

  const enableBlocked = form.enabled && noOffers
  const canSave = form.couponCode.trim() !== '' && coupon !== null && !saving && !enableBlocked

  const handleSave = async () => {
    if (!canSave) return
    setSaving(true)
    try {
      if (isEdit) {
        await updatePromotion(promotionId!, {
          starts_at: localInputToIso(form.startsAt),
          ends_at: localInputToIso(form.endsAt),
          enabled: form.enabled,
        })
      } else {
        await createPromotion({
          coupon_code: form.couponCode.trim(),
          starts_at: localInputToIso(form.startsAt),
          ends_at: localInputToIso(form.endsAt),
          enabled: form.enabled,
        })
      }
      toast(`Promotion ${isEdit ? 'saved' : 'created'}`, 'success')
      onSaved()
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to save promotion', 'error')
    } finally {
      setSaving(false)
    }
  }

  const footer = (
    <>
      <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
      <Button onClick={() => void handleSave()} disabled={!canSave}>
        {saving ? 'Saving…' : isEdit ? 'Save' : 'Create'}
      </Button>
    </>
  )

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Edit promotion' : 'New promotion'}
      footer={footer}
    >
      <div className={formStyles.formField}>
        {/* Coupon */}
        <div className={formStyles.fieldGroup}>
          <label>Cleeng coupon code</label>
          {isEdit ? (
            <input type="text" value={form.couponCode} readOnly disabled />
          ) : (
            <div className={styles.couponRow}>
              <input
                type="text"
                value={form.couponCode}
                onChange={(e) => setForm((p) => ({ ...p, couponCode: e.target.value }))}
                onBlur={(e) => void loadCoupon(e.target.value)}
                placeholder="e.g. SUMMER25"
                disabled={saving}
              />
              <Button
                variant="secondary"
                onClick={() => void loadCoupon(form.couponCode)}
                disabled={couponLoading || !form.couponCode.trim()}
              >
                {couponLoading ? 'Loading…' : 'Load'}
              </Button>
            </div>
          )}
          {couponError && <div className={formStyles.errorText}>{couponError}</div>}
          <p className={formStyles.hint}>
            {isEdit
              ? 'The coupon is fixed. To use a different one, delete this promotion and create a new one.'
              : 'Coupons are created in the Cleeng console. The promotion applies to every offer the coupon covers.'}
          </p>
        </div>

        {/* Resolved coupon summary */}
        {coupon && (
          <div className={styles.couponSummary}>
            <div className={styles.couponSummaryHead}>
              <strong>{coupon.name || coupon.code}</strong>
              <Badge variant={couponStatusVariant(coupon.status)}>{coupon.status}</Badge>
              <span className={styles.discountPill}>{pct(coupon.discount)}% off</span>
            </div>
            {!ACTIVE_COUPON_STATUSES.has(coupon.status) && (
              <p className={formStyles.hint}>
                This coupon is not active in Cleeng, so enabling the promotion may surface a code the
                provider rejects.
              </p>
            )}
          </div>
        )}

        {/* Paywall preview: every offer the coupon applies to */}
        {coupon && (
          <div className={styles.preview}>
            <h4>Paywall preview</h4>
            {noOffers ? (
              <p className={formStyles.errorText}>
                This coupon covers no offers. Check its targeting rules in Cleeng.
              </p>
            ) : (
              <>
                {contestedCount > 0 && (
                  <p className={overriddenCount > 0 ? formStyles.errorText : formStyles.hint}>
                    {contestedCount} of {eligibleOffers.length} offer
                    {eligibleOffers.length === 1 ? '' : 's'} also covered by another promotion
                    {overriddenCount > 0
                      ? ` — ${overriddenCount} would be overridden, so shoppers there see a different discount than below.`
                      : ' — this promotion wins on each.'}
                  </p>
                )}
                <table className={styles.previewTable}>
                  <thead>
                    <tr>
                      <th>Offer</th>
                      <th>Was</th>
                      <th>Now</th>
                    </tr>
                  </thead>
                  <tbody>
                    {eligibleOffers.map((offer) => {
                      if (offer.price_amount == null) {
                        return (
                          <tr key={offer.id}>
                            <td>{offer.title || offer.id}</td>
                            <td colSpan={2} className={styles.muted}>no price</td>
                          </tr>
                        )
                      }
                      const conflict = conflictByOffer.get(offer.id)
                      const candidate = conflict?.promotions.find((p) => p.is_candidate)
                      const winner = conflict?.promotions.find((p) => p.wins)
                      const overridden = !!candidate && !candidate.wins && !!winner
                      const now = discountedPrice(offer.price_amount, coupon.discount)
                      const winnerNow =
                        winner ? discountedPrice(offer.price_amount, winner.discount) : now
                      return (
                        <tr key={offer.id}>
                          <td>
                            {offer.title || offer.id}
                            {overridden && winner && (
                              <div className={formStyles.errorText}>
                                Overridden by {winner.tc_name || winner.coupon_code} (
                                {pct(winner.discount)}% off) during the overlap
                              </div>
                            )}
                            {conflict && !overridden && (
                              <div className={formStyles.hint}>
                                Also promoted by another coupon here; this one wins
                              </div>
                            )}
                          </td>
                          <td className={styles.was}>
                            {formatMoney(offer.price_amount, offer.price_currency)}
                          </td>
                          <td>
                            {overridden ? (
                              <>
                                <span className={styles.was}>
                                  {formatMoney(now, offer.price_currency)}
                                </span>{' '}
                                <span className={styles.now}>
                                  {formatMoney(winnerNow, offer.price_currency)}
                                </span>
                              </>
                            ) : (
                              <span className={styles.now}>{formatMoney(now, offer.price_currency)}</span>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </>
            )}
          </div>
        )}

        {/* Display window */}
        <div className={styles.windowRow}>
          <div className={formStyles.fieldGroup}>
            <label>Starts</label>
            <input
              type="datetime-local"
              value={form.startsAt}
              onChange={(e) => setForm((p) => ({ ...p, startsAt: e.target.value }))}
              disabled={saving}
            />
          </div>
          <div className={formStyles.fieldGroup}>
            <label>Ends</label>
            <input
              type="datetime-local"
              value={form.endsAt}
              onChange={(e) => setForm((p) => ({ ...p, endsAt: e.target.value }))}
              disabled={saving}
            />
          </div>
        </div>
        <p className={formStyles.hint}>
          Keep the window inside the Cleeng coupon’s own validity.
        </p>

        {/* Enable */}
        <div className={formStyles.fieldGroup}>
          <label className={formStyles.checkboxLabel}>
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(e) => setForm((p) => ({ ...p, enabled: e.target.checked }))}
              disabled={saving}
            />
            Enabled: surface this promotion in the apps during its window
          </label>
          {enableBlocked && (
            <p className={formStyles.errorText}>
              Can’t enable while the coupon covers no offers. Fix its targeting in Cleeng, or turn
              this off to save as a draft.
            </p>
          )}
        </div>
      </div>
    </Drawer>
  )
}
